import type { ArtistTier, Complexity, Placement, Region } from '@/lib/calculators'
import {
  ARTIST_TIERS,
  calculateTattooCost,
  COMPLEXITY_MULTIPLIERS,
  PLACEMENT_MULTIPLIERS,
  REGION_MULTIPLIERS,
  STUDIO_MINIMUM,
} from '@/lib/calculators'
import SaveEstimateButton from '@/components/SaveEstimateButton'
import EmailEstimateButton from '@/components/EmailEstimateButton'
import {CreditCard, Ruler, Smartphone} from 'lucide-react'
import { useMemo, useState } from 'react'

const SIZE_REFERENCES = [
  { label: 'Coin', sqIn: 1, icon: '🪙' },
  { label: 'Credit Card', sqIn: 3.375, icon: '💳' },
  { label: 'Smartphone', sqIn: 13.5, icon: '📱' },
  { label: 'Paperback Book', sqIn: 42, icon: '📖' },
  { label: 'Laptop', sqIn: 90, icon: '💻' },
]

function TattooCalculator() {
  const [width, setWidth] = useState(4)
  const [height, setHeight] = useState(4)
  const [placement, setPlacement] = useState<Placement>('forearm')
  const [complexity, setComplexity] = useState<Complexity>('shaded')
  const [artistTier, setArtistTier] = useState<ArtistTier>('professional')
  const [region, setRegion] = useState<Region>('standard')

  const result = useMemo(
    () => calculateTattooCost({ widthIn: width, heightIn: height, placement, complexity, artistTier, region }),
    [width, height, placement, complexity, artistTier, region],
  )

  const closestReference = useMemo(() => {
    return SIZE_REFERENCES.reduce((closest, ref) => {
      return Math.abs(ref.sqIn - result.squareInches) < Math.abs(closest.sqIn - result.squareInches) ? ref : closest
    }, SIZE_REFERENCES[0])
  }, [result.squareInches])

  return (
    <section id="tattoo-calculator" className="bg-[#0e0c0a] px-4 py-20 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-serif text-3xl font-bold text-white sm:text-4xl">
            Tattoo Cost Calculator
          </h2>
          <p className="mt-3 text-slate-400">
            Calculate tattoo cost instantly by size, placement, style, and artist tier. See the exact average tattoo
            cost breakdown before you book.
          </p>
        </div>

        <div className="mt-12 grid gap-8 lg:grid-cols-5">
          {/* Inputs */}
          <div className="space-y-6 rounded-2xl border border-white/10 bg-white/[0.03] p-6 lg:col-span-3 lg:p-8">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="tattoo-width" className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-300">
                  <Ruler className="h-4 w-4 text-amber-400" aria-hidden="true" />
                  Width (inches)
                </label>
                <input
                  id="tattoo-width"
                  type="number"
                  min={0.5}
                  step={0.5}
                  value={width}
                  onChange={e => setWidth(Number.parseFloat(e.target.value) || 0)}
                  className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 text-lg text-white focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/30"
                />
              </div>
              <div>
                <label htmlFor="tattoo-height" className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-300">
                  <Ruler className="h-4 w-4 text-amber-400 rotate-90" aria-hidden="true" />
                  Height (inches)
                </label>
                <input
                  id="tattoo-height"
                  type="number"
                  min={0.5}
                  step={0.5}
                  value={height}
                  onChange={e => setHeight(Number.parseFloat(e.target.value) || 0)}
                  className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 text-lg text-white focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/30"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 rounded-lg bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
              <CreditCard className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              <span>
                {result.squareInches}
                {' '}
                sq. in. &mdash; about the size of a
                {' '}
                <strong>{closestReference.label}</strong>
                {' '}
                {closestReference.icon}
              </span>
            </div>

            <div>
              <label htmlFor="placement" className="mb-2 block text-sm font-medium text-slate-300">Placement</label>
              <select
                id="placement"
                value={placement}
                onChange={e => setPlacement(e.target.value as Placement)}
                className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 text-white focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/30"
              >
                {Object.entries(PLACEMENT_MULTIPLIERS).map(([key, val]) => (
                  <option key={key} value={key}>{val.label} ({val.multiplier}x pain factor)</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="complexity" className="mb-2 block text-sm font-medium text-slate-300">Design Complexity</label>
              <select
                id="complexity"
                value={complexity}
                onChange={e => setComplexity(e.target.value as Complexity)}
                className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 text-white focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/30"
              >
                {Object.entries(COMPLEXITY_MULTIPLIERS).map(([key, val]) => (
                  <option key={key} value={key}>{val.label} ({val.multiplier}x)</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="artist-tier" className="mb-2 block text-sm font-medium text-slate-300">Artist Tier</label>
                <select
                  id="artist-tier"
                  value={artistTier}
                  onChange={e => setArtistTier(e.target.value as ArtistTier)}
                  className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 text-white focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/30"
                >
                  {Object.entries(ARTIST_TIERS).map(([key, val]) => (
                    <option key={key} value={key}>{val.label} (${val.rate}/hr)</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="region" className="mb-2 block text-sm font-medium text-slate-300">Region</label>
                <select
                  id="region"
                  value={region}
                  onChange={e => setRegion(e.target.value as Region)}
                  className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 text-white focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/30"
                >
                  {Object.entries(REGION_MULTIPLIERS).map(([key, val]) => (
                    <option key={key} value={key}>{val.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Output */}
          <div className="rounded-2xl border border-amber-500/20 bg-gradient-to-b from-amber-500/[0.08] to-transparent p-6 lg:col-span-2 lg:p-8">
            <h3 className="font-serif text-xl font-semibold text-white">Your Estimate</h3>
            <p className="mt-1 text-sm text-slate-400">Real-time breakdown based on studio industry averages</p>

            <div className="mt-6 rounded-xl bg-black/30 p-6 text-center">
              <p className="text-xs uppercase tracking-widest text-amber-300">Total Estimated Cost</p>
              <p className="mt-2 font-serif text-4xl font-bold text-amber-400">
                $
                {result.total.toLocaleString()}
              </p>
              {!result.meetsMinimum && (
                <p className="mt-2 text-xs text-slate-500">
                  Studio minimum of ${STUDIO_MINIMUM} applied
                </p>
              )}
            </div>

            <dl className="mt-6 space-y-3 text-sm">
              <div className="flex justify-between border-b border-white/5 pb-2">
                <dt className="text-slate-400">Estimated Hours</dt>
                <dd className="font-medium text-white">{result.estimatedHours} hrs</dd>
              </div>
              <div className="flex justify-between border-b border-white/5 pb-2">
                <dt className="text-slate-400">Artist Rate</dt>
                <dd className="font-medium text-white">
                  $
                  {result.hourlyRate}
                  /hr
                </dd>
              </div>
              <div className="flex justify-between border-b border-white/5 pb-2">
                <dt className="text-slate-400">Labor Cost</dt>
                <dd className="font-medium text-white">
                  $
                  {result.laborCost.toLocaleString()}
                </dd>
              </div>
              <div className="flex justify-between border-b border-white/5 pb-2">
                <dt className="text-slate-400">Materials Fee</dt>
                <dd className="font-medium text-white">
                  $
                  {result.materialsFee}
                </dd>
              </div>
              <div className="flex justify-between border-b border-white/5 pb-2">
                <dt className="text-slate-400">Studio Minimum</dt>
                <dd className="font-medium text-white">
                  $
                  {STUDIO_MINIMUM}
                </dd>
              </div>
              <div className="flex justify-between pt-1">
                <dt className="text-amber-300">Recommended Tip (18%)</dt>
                <dd className="font-semibold text-amber-300">
                  $
                  {result.recommendedTip}
                </dd>
              </div>
            </dl>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <SaveEstimateButton
                calculatorType="tattoo"
                summary={`${PLACEMENT_MULTIPLIERS[placement].label} tattoo, ${result.squareInches} sq. in., ${COMPLEXITY_MULTIPLIERS[complexity].label} \u2014 estimated total $${result.total.toLocaleString()}`}
                inputs={{ width, height, placement, complexity, artistTier, region }}
                results={result}
                className="flex w-full items-center justify-center gap-2 rounded-full border border-amber-400/40 bg-amber-500/10 px-5 py-3 text-sm font-semibold text-amber-300 transition-colors hover:bg-amber-500/20 disabled:cursor-not-allowed disabled:opacity-60"
              />
              <EmailEstimateButton
                calculatorType="tattoo"
                summary={`${PLACEMENT_MULTIPLIERS[placement].label} tattoo, ${result.squareInches} sq. in., ${COMPLEXITY_MULTIPLIERS[complexity].label} \u2014 estimated total $${result.total.toLocaleString()}`}
                inputs={{ width, height, placement, complexity, artistTier, region }}
                results={result}
                className="flex w-full items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-slate-200 transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default TattooCalculator
